import React from 'react';
import { useTranslation } from 'react-i18next';
import IAccountProps from '../../types/IAccountProps';
import getSymbolFromCurrency from 'currency-symbol-map';
import { mapToSubAccountType, maskAccountNumber } from '../../domain/services/BankAccountModifierService';
import FeatherIcon from 'feather-icons-react';
import { IAccountType } from '../../domain/services/IConnectApi';
import { useSettings } from '../../hooks/queryHooks';

interface IProps {
  account: IAccountProps;
  bankName?: string | null;
  accountSubType?: IAccountType;
}

const formatBalance = (balance: number, currencyCode: string) => {
  if (balance >= 0) {
    return `${getSymbolFromCurrency(currencyCode)}${balance.toFixed(2)}`;
  }

  const balanceStr = balance.toFixed(2).slice(1);

  return `-${getSymbolFromCurrency(currencyCode)}${balanceStr}`;
};

const ConnectionSummaryTile: React.FC<IProps> = ({ account, bankName, accountSubType }: IProps) => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  return (
    <div
      className={`${
        !settings?.enableCreditInformationFlow && 'px-4 sm:px-6 py-4'
      } pb-4 pt-2 text-primary-2 flex justify-between items-start`}
      data-testid="connection-summary-tile"
    >
      <div className="flex flex-col justify-between items-start w-full">
        {bankName && (
          <span className="block text-md sm:text-lg font-normal">
            {bankName}
            {accountSubType ? `: ${mapToSubAccountType(accountSubType)}` : ''}
          </span>
        )}
        {typeof account.currentBalance !== 'undefined' && account.currentBalance !== null && (
          <span
            className={`text-base font-medium sm:mt-1 text-primary-2 block ${
              settings?.enableCreditInformationFlow ? 'mb-1' : 'mb-4'
            }`}
          >
            {`${t<string>('Balance')}: ${formatBalance(account.currentBalance, account?.currencyCode || '')}`}
          </span>
        )}
        <div className="flex items-center mt-2 sm:mt-0">
          <span className="text-gray-500 sm:mr-0 mr-1">
            {' '}
            {settings?.enableCreditInformationFlow ? t('Account ref') : t('Account no')}:
          </span>
          <span className="font-medium">{maskAccountNumber(account.accountNumber)}</span>
        </div>
      </div>
      <div className="flex">
        <FeatherIcon icon="check-circle" size={20} className="text-primary-1" />
        <FeatherIcon icon="lock" className="text-gray-500 ml-2" size={20} strokeWidth={1.5} />
      </div>
    </div>
  );
};

export default ConnectionSummaryTile;
